import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import GiftSendModal from "@/components/GiftSendModal";
import ProtectedImage from "@/components/ProtectedImage";

const THEME_BG = {
  classic: { bg: "linear-gradient(180deg,#0a0a0b 0%,#0a0a0b 100%)", accent: "#D4AF37" },
  heavenly: { bg: "linear-gradient(180deg,#1e2a44 0%,#3a5688 50%,#cfd9e8 100%)", accent: "#cfe1ff" },
  faith: { bg: "linear-gradient(180deg,#1b1024 0%,#3a2a55 100%)", accent: "#cf9fff" },
  celebration: { bg: "linear-gradient(180deg,#1f0d05 0%,#5a3122 100%)", accent: "#ffb27a" },
  military: { bg: "linear-gradient(180deg,#091628 0%,#1b3556 100%)", accent: "#7da8e0" },
  garden: { bg: "linear-gradient(180deg,#0d1a10 0%,#2a4a30 100%)", accent: "#9bd1a8" },
};

export default function MemorialPage() {
  const { slug } = useParams();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [candles, setCandles] = useState([]);
  const [gb, setGb] = useState([]);
  const [name, setName] = useState("");
  const [msg, setMsg] = useState("");
  const [candleName, setCandleName] = useState("");
  const [candleMsg, setCandleMsg] = useState("");
  const [partners, setPartners] = useState([]);
  const [giftOpen, setGiftOpen] = useState(false);

  const [donations, setDonations] = useState({ items: [], total_usd: 0, count: 0, goal_usd: 0, cause: null, donations_active: false });
  const [donateOpen, setDonateOpen] = useState(false);
  const [donateForm, setDonateForm] = useState({ amount: 25, donor_name: "", donor_email: "", donor_message: "" });
  const [donateBusy, setDonateBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/memorial/by-slug/${slug}`);
      setM(data);
      const [c, g, p, d] = await Promise.all([
        api.get(`/memorial/${data.id}/candles`),
        api.get(`/memorial/${data.id}/guestbook`),
        api.get(`/extras/sympathy-gifts`),
        api.get(`/donations/${data.id}`).catch(() => ({ data: null })),
      ]);
      setCandles(c.data.items || []);
      setGb(g.data.items || []);
      setPartners(p.data.partners || []);
      if (d.data) setDonations(d.data);
    } catch (e) {
      // 404 etc.
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [slug]);

  // Detect ?donated=1 redirect from Stripe Checkout success
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("donated") === "1") {
      toast.success("Thank you for your generous donation. The family will be deeply grateful.", { duration: 8000 });
      // Strip the query param so refreshing doesn't re-toast
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const lightCandle = async () => {
    try {
      await api.post("/memorial/candle", { obituary_id: m.id, visitor_name: candleName || "Anonymous friend", message: candleMsg });
      setCandleName(""); setCandleMsg("");
      toast.success("Your candle is lit.");
      load();
    } catch { toast.error("Could not light candle."); }
  };

  const submitGuestbook = async () => {
    if (!name.trim() || !msg.trim()) return toast.error("Please add your name and a message.");
    try {
      await api.post("/memorial/guestbook", { obituary_id: m.id, visitor_name: name, message: msg });
      setName(""); setMsg("");
      toast.success("Thank you. Your message has been added.");
      load();
    } catch { toast.error("Could not post message."); }
  };

  if (loading) return <div style={{ maxWidth: 700, margin: "80px auto", padding: 24 }}><div className="shimmer" style={{ height: 320, borderRadius: 18 }} /></div>;
  if (!m) return (
    <div style={{ maxWidth: 600, margin: "120px auto", padding: 30, textAlign: "center" }}>
      <div className="serif" style={{ fontSize: 36, color: "var(--gold)" }}>Memorial not found</div>
      <p className="muted" style={{ marginTop: 16 }}>This memorial may be private or has been removed.</p>
    </div>
  );

  const theme = THEME_BG[m.theme] || THEME_BG.classic;

  return (
    <div data-testid="memorial-page" style={{ minHeight: "100vh", background: theme.bg, paddingBottom: 80 }}>
      <div style={{ maxWidth: 720, margin: "0 auto", padding: "60px 24px 40px", textAlign: "center" }}>
        <div style={{ fontSize: 11, letterSpacing: "0.4em", color: theme.accent, marginBottom: 18, opacity: 0.85 }}>IN LOVING MEMORY</div>
        {m.portrait_b64 && (
          <ProtectedImage
            src={`data:image/png;base64,${m.portrait_b64}`}
            alt={m.deceased_name}
            wrapStyle={{ width: 200, height: 200, borderRadius: "50%", marginBottom: 24, border: `3px solid ${theme.accent}` }}
            imgStyle={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
          />
        )}
        <h1 className="serif" style={{ fontSize: 48, color: "#fff", margin: 0, fontWeight: 400, letterSpacing: "0.02em" }}>{m.deceased_name}</h1>
        {(m.born || m.died) && <div style={{ color: "#ddd", marginTop: 10, fontStyle: "italic", fontSize: 16, opacity: 0.85 }}>{m.born || "?"} — {m.died || "?"}</div>}
        <div style={{ marginTop: 30, fontSize: 14, color: theme.accent, opacity: 0.85 }}>
          🕯 {m.candles_lit || 0} candles lit · ✉ {m.guestbook_count || 0} guestbook messages
        </div>
        <div style={{ marginTop: 22, display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
          <button data-testid="memorial-send-gift" onClick={() => setGiftOpen(true)} className="btn-gold" style={{ padding: "12px 24px" }}>
            🎁 Send a sympathy gift
          </button>
          {donations.donations_active && (
            <button
              data-testid="memorial-donate"
              onClick={() => setDonateOpen(true)}
              className="btn-ghost"
              style={{ padding: "12px 24px" }}
            >
              💝 Donate to the family
            </button>
          )}
        </div>
      </div>

      {donations.donations_active && (
        <div data-testid="donation-widget" style={{ maxWidth: 620, margin: "10px auto 0", padding: "0 24px" }}>
          <div className="bg-card" style={{ padding: 22, borderRadius: 14 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
              <div>
                <div style={{ fontSize: 10, letterSpacing: "0.3em", color: theme.accent, marginBottom: 4 }}>MEMORIAL FUND</div>
                <div className="serif" style={{ fontSize: 22, color: "#fff" }}>{donations.fund_name || `In memory of ${m.deceased_name}`}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div className="serif" style={{ fontSize: 28, color: theme.accent, fontWeight: 600 }}>${donations.total_usd.toFixed(0)}</div>
                {donations.goal_usd > 0 && <div className="muted" style={{ fontSize: 11 }}>of ${donations.goal_usd.toFixed(0)} goal</div>}
              </div>
            </div>
            {donations.cause && (
              <div className="muted" style={{ fontSize: 13, marginBottom: 14, fontStyle: "italic", lineHeight: 1.5 }}>&ldquo;{donations.cause}&rdquo;</div>
            )}
            {donations.goal_usd > 0 && (
              <div style={{ height: 10, background: "rgba(255,255,255,0.08)", borderRadius: 999, overflow: "hidden", marginBottom: 12 }}>
                <div style={{
                  height: "100%",
                  width: `${Math.min(100, (donations.total_usd / donations.goal_usd) * 100)}%`,
                  background: `linear-gradient(90deg, ${theme.accent}, ${theme.accent}aa)`,
                  transition: "width 1s ease-out",
                }} />
              </div>
            )}
            <div className="muted" style={{ fontSize: 12, marginBottom: 14 }}>
              <b style={{ color: "#fff" }}>{donations.count}</b> donor{donations.count === 1 ? "" : "s"} · 90% goes directly to the family (10% platform fee supports the memorial site)
            </div>
            <button data-testid="donation-widget-donate-btn" onClick={() => setDonateOpen(true)} className="btn-gold" style={{ width: "100%", padding: "12px 22px" }}>
              💝 Donate to support the family
            </button>
            {donations.items.length > 0 && (
              <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <div style={{ fontSize: 10, letterSpacing: "0.2em", color: "#888", marginBottom: 10 }}>RECENT DONORS</div>
                {donations.items.slice(0, 5).map(d => (
                  <div key={d.id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13, color: "#ddd" }}>
                    <div>
                      <b style={{ color: "#fff" }}>{d.donor_name || "Anonymous"}</b>
                      {d.donor_message && <span className="muted" style={{ marginLeft: 6, fontStyle: "italic" }}>&mdash; {d.donor_message.slice(0, 60)}</span>}
                    </div>
                    <div style={{ color: theme.accent, fontWeight: 600 }}>${(d.actual_amount_usd || d.amount_usd).toFixed(0)}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {donateOpen && (
        <div data-testid="donate-modal-overlay" onClick={() => setDonateOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
        }}>
          <div onClick={e => e.stopPropagation()} className="bg-card" style={{ maxWidth: 480, width: "100%", padding: 28, borderRadius: 14 }}>
            <div style={{ fontSize: 11, letterSpacing: "0.3em", color: theme.accent, marginBottom: 4 }}>MEMORIAL DONATION</div>
            <div className="serif" style={{ fontSize: 24, color: "#fff", marginBottom: 6 }}>Donate to {m.deceased_name}&apos;s family</div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 18 }}>90% goes directly to the family via secure Stripe transfer (10% platform fee).</div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              if (!(donateForm.amount > 1)) return toast.error("Minimum donation is $2.");
              setDonateBusy(true);
              try {
                const { data } = await api.post("/donations/checkout", {
                  obituary_id: m.id,
                  amount_usd: donateForm.amount,
                  donor_name: donateForm.donor_name || "A friend",
                  donor_email: donateForm.donor_email || null,
                  donor_message: donateForm.donor_message,
                  origin_url: window.location.origin,
                });
                if (data.checkout_url) window.location.href = data.checkout_url;
              } catch (err) {
                toast.error(err?.response?.data?.detail || "Could not start donation.");
                setDonateBusy(false);
              }
            }} style={{ display: "grid", gap: 12 }}>
              <div>
                <label className="label-dark">Amount (USD)</label>
                <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap" }}>
                  {[10, 25, 50, 100, 250].map(amt => (
                    <button type="button" key={amt} data-testid={`donate-preset-${amt}`} onClick={() => setDonateForm(f => ({ ...f, amount: amt }))} style={{
                      padding: "8px 14px", borderRadius: 999, cursor: "pointer", fontSize: 13, fontFamily: "inherit",
                      background: donateForm.amount === amt ? theme.accent : "transparent",
                      color: donateForm.amount === amt ? "#0a0a0b" : "#ddd",
                      border: "1px solid " + (donateForm.amount === amt ? theme.accent : "#2a2a2a"),
                      fontWeight: donateForm.amount === amt ? 700 : 400,
                    }}>${amt}</button>
                  ))}
                </div>
                <input data-testid="donate-amount" type="number" min="2" max="10000" step="1" value={donateForm.amount} onChange={e => setDonateForm({ ...donateForm, amount: parseFloat(e.target.value) || 0 })} className="input-dark" />
              </div>
              <div>
                <label className="label-dark">Your name</label>
                <input data-testid="donate-name" value={donateForm.donor_name} onChange={e => setDonateForm({ ...donateForm, donor_name: e.target.value })} className="input-dark" placeholder="Anonymous if blank" />
              </div>
              <div>
                <label className="label-dark">Email (for receipt — optional)</label>
                <input data-testid="donate-email" type="email" value={donateForm.donor_email} onChange={e => setDonateForm({ ...donateForm, donor_email: e.target.value })} className="input-dark" placeholder="you@email.com" />
              </div>
              <div>
                <label className="label-dark">Message to the family (optional)</label>
                <textarea data-testid="donate-message" rows={2} value={donateForm.donor_message} onChange={e => setDonateForm({ ...donateForm, donor_message: e.target.value })} className="input-dark" placeholder="Share a kind word…" />
              </div>
              <button data-testid="donate-submit" type="submit" disabled={donateBusy} className="btn-gold" style={{ padding: 14, marginTop: 4 }}>
                {donateBusy ? "Opening checkout…" : `Donate $${donateForm.amount || 0} →`}
              </button>
            </form>
          </div>
        </div>
      )}

      <GiftSendModal open={giftOpen} onClose={() => setGiftOpen(false)} obituaryId={m.id} recipientHint={{ name: m.deceased_name }} />

      {m.body && (
        <div style={{ maxWidth: 680, margin: "0 auto", padding: "0 24px", color: "#e6e6e6", fontSize: 16, lineHeight: 1.85, whiteSpace: "pre-wrap" }}>
          {m.body}
        </div>
      )}

      {m.photos && m.photos.length > 0 && (
        <div style={{ maxWidth: 1000, margin: "40px auto", padding: "0 24px", display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
          {m.photos.map((p, i) => (
            <ProtectedImage
              key={i}
              src={`data:image/jpeg;base64,${p}`}
              alt={`Memory ${i+1}`}
              wrapStyle={{ width: "100%", aspectRatio: "4/3", borderRadius: 10 }}
              imgStyle={{ width: "100%", height: "100%", borderRadius: 10, objectFit: "cover" }}
            />
          ))}
        </div>
      )}

      {/* ── Light a candle ── */}
      <div style={{ maxWidth: 620, margin: "50px auto 0", padding: "0 24px" }}>
        <div className="bg-card" style={{ padding: 26 }}>
          <div className="serif" style={{ fontSize: 24, color: "#fff", marginBottom: 14, textAlign: "center" }}>🕯 Light a candle</div>
          <input data-testid="candle-name" value={candleName} onChange={e => setCandleName(e.target.value)} placeholder="Your name (optional)" style={{ width: "100%", padding: 12, marginBottom: 10, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
          <input data-testid="candle-message" value={candleMsg} onChange={e => setCandleMsg(e.target.value)} placeholder="A short message (optional)" style={{ width: "100%", padding: 12, marginBottom: 12, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
          <button data-testid="candle-btn" onClick={lightCandle} className="btn-gold" style={{ width: "100%" }}>Light a candle</button>
        </div>
        {candles.length > 0 && (
          <div style={{ marginTop: 18, display: "flex", flexWrap: "wrap", gap: 10, justifyContent: "center" }}>
            {candles.slice(0, 36).map(c => (
              <div key={c.id} title={`${c.visitor_name}${c.message ? ' — ' + c.message : ''}`} style={{ fontSize: 24, opacity: 0.9 }}>🕯</div>
            ))}
          </div>
        )}
      </div>

      {/* ── Guestbook ── */}
      <div style={{ maxWidth: 620, margin: "40px auto 0", padding: "0 24px" }}>
        <div className="serif" style={{ fontSize: 26, color: "#fff", textAlign: "center", marginBottom: 14 }}>Guestbook</div>
        <div className="bg-card" style={{ padding: 22, marginBottom: 18 }}>
          <input data-testid="gb-name" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" style={{ width: "100%", padding: 12, marginBottom: 10, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8 }} />
          <textarea data-testid="gb-message" value={msg} onChange={e => setMsg(e.target.value)} placeholder="Share a memory…" rows={4} style={{ width: "100%", padding: 12, marginBottom: 12, background: "#0a0a0b", border: "1px solid #2a2a2a", color: "#fff", borderRadius: 8, resize: "vertical" }} />
          <button data-testid="gb-submit" onClick={submitGuestbook} className="btn-gold" style={{ width: "100%" }}>Add to the guestbook</button>
        </div>
        {gb.map(g => (
          <div key={g.id} className="bg-card" style={{ padding: 18, marginBottom: 10 }}>
            <div style={{ color: "#fff", fontSize: 14, marginBottom: 6 }}><b>{g.visitor_name}</b> <span className="muted" style={{ fontSize: 11 }}>{new Date(g.created_at).toLocaleDateString()}</span></div>
            <div style={{ color: "#ddd", lineHeight: 1.6, fontSize: 14, whiteSpace: "pre-wrap" }}>{g.message}</div>
          </div>
        ))}
      </div>

      {/* ── Sympathy gifts / donations ── */}
      {partners.length > 0 && (
        <div style={{ maxWidth: 620, margin: "50px auto 0", padding: "0 24px" }}>
          <div className="serif" style={{ fontSize: 20, color: "#fff", textAlign: "center", marginBottom: 12 }}>Ways to honor their memory</div>
          {partners.map(p => (
            <a key={p.url} href={p.url} target="_blank" rel="noreferrer" className="bg-card" style={{ display: "block", padding: 16, marginBottom: 8, textDecoration: "none" }}>
              <div style={{ color: "#fff", fontSize: 15, marginBottom: 4 }}>{p.name}</div>
              <div className="muted" style={{ fontSize: 13 }}>{p.blurb}</div>
            </a>
          ))}
          <div className="muted" style={{ fontSize: 10, textAlign: "center", marginTop: 8 }}>Some partner links may share a small commission with Obituary Relief.</div>
        </div>
      )}

      <div style={{ textAlign: "center", marginTop: 70, color: "#888", fontSize: 12 }}>— Memorial composed with Obituary Relief —</div>
    </div>
  );
}
